import { useState, useEffect } from 'react';
import axios from 'axios';
import TextField from '@mui/material/TextField';
import MenuItem from '@mui/material/MenuItem';
import FormControlLabel from '@mui/material/FormControlLabel';
import Checkbox from '@mui/material/Checkbox';
import { useSnackbar } from 'notistack';
import { useNavigate, useParams } from 'react-router-dom';
import MetaData from '../Layouts/MetaData';
import BackdropLoader from '../Layouts/BackdropLoader';

const discountLabels = { flat: "Flat Amount Off (HK$)", percent: "Percentage Off (%)", free_delivery: "Free Delivery" };

const PromoCodeForm = () => {
    const { id } = useParams();
    const isEditMode = Boolean(id);

    const { enqueueSnackbar } = useSnackbar();
    const navigate = useNavigate();

    const [loading, setLoading] = useState(isEditMode);
    const [saving, setSaving] = useState(false);

    const [code, setCode] = useState("");
    const [description, setDescription] = useState("");
    const [discountType, setDiscountType] = useState("flat");
    const [discountValue, setDiscountValue] = useState(100);
    const [maxDiscountAmount, setMaxDiscountAmount] = useState("");
    const [minOrderValue, setMinOrderValue] = useState(0);
    const [expiryDate, setExpiryDate] = useState("");
    const [usageLimitTotal, setUsageLimitTotal] = useState("");
    const [usageLimitPerUser, setUsageLimitPerUser] = useState(1);
    const [active, setActive] = useState(true);

    useEffect(() => {
        if (!isEditMode) {
            setLoading(false);
            return;
        }
        const fetchPromoCode = async () => {
            try {
                const { data } = await axios.get('/api/v1/admin/promo-codes');
                const promo = data.promoCodes.find((p) => p._id === id);
                if (!promo) {
                    enqueueSnackbar("Promo code not found", { variant: "error" });
                    navigate('/admin/promo-codes');
                    return;
                }
                setCode(promo.code);
                setDescription(promo.description || "");
                setDiscountType(promo.discountType);
                setDiscountValue(promo.discountValue ?? 100);
                setMaxDiscountAmount(promo.maxDiscountAmount ?? "");
                setMinOrderValue(promo.minOrderValue ?? 0);
                setExpiryDate(promo.expiryDate ? new Date(promo.expiryDate).toISOString().substring(0, 10) : "");
                setUsageLimitTotal(promo.usageLimitTotal ?? "");
                setUsageLimitPerUser(promo.usageLimitPerUser ?? 1);
                setActive(promo.active);
            } catch (error) {
                enqueueSnackbar(error?.response?.data?.message || "Failed to load promo code", { variant: "error" });
            } finally {
                setLoading(false);
            }
        };
        fetchPromoCode();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id]);

    const submitHandler = async (e) => {
        e.preventDefault();

        if (!code.trim()) return enqueueSnackbar("Please enter a code", { variant: "warning" });
        if (discountType !== "free_delivery" && !discountValue) return enqueueSnackbar("Please enter a discount value", { variant: "warning" });

        const payload = {
            code, description, discountType,
            discountValue: discountType === "free_delivery" ? undefined : discountValue,
            maxDiscountAmount: discountType === "percent" && maxDiscountAmount ? maxDiscountAmount : undefined,
            minOrderValue,
            expiryDate: expiryDate || undefined,
            usageLimitTotal: usageLimitTotal || undefined,
            usageLimitPerUser,
            active,
        };

        setSaving(true);
        try {
            if (isEditMode) {
                await axios.put(`/api/v1/admin/promo-code/${id}`, payload);
                enqueueSnackbar("Promo code updated", { variant: "success" });
            } else {
                await axios.post('/api/v1/admin/promo-code/new', payload);
                enqueueSnackbar("Promo code created", { variant: "success" });
            }
            navigate('/admin/promo-codes');
        } catch (error) {
            enqueueSnackbar(error?.response?.data?.message || "Failed to save promo code", { variant: "error" });
        } finally {
            setSaving(false);
        }
    };

    if (loading) return <BackdropLoader />;

    return (
        <>
            <MetaData title={`Admin: ${isEditMode ? "Edit" : "New"} Promo Code | 1313 Mart`} />
            {saving && <BackdropLoader />}

            <form onSubmit={submitHandler} className="flex flex-col gap-4 bg-white rounded-lg shadow p-4 sm:p-6" id="promocodeform">

                <h1 className="text-lg font-medium uppercase">{isEditMode ? "Edit" : "New"} Promo Code</h1>

                <div className="flex flex-col sm:flex-row gap-4">
                    <TextField label="Code" variant="outlined" size="small" required fullWidth value={code} onChange={(e) => setCode(e.target.value.toUpperCase())} helperText="Customers type this exactly — auto-uppercased" />
                    <TextField label="Internal Note (optional)" variant="outlined" size="small" fullWidth value={description} onChange={(e) => setDescription(e.target.value)} />
                </div>

                <div className="flex flex-col sm:flex-row gap-4">
                    <TextField label="Discount Type" select variant="outlined" size="small" value={discountType} onChange={(e) => setDiscountType(e.target.value)} sx={{ minWidth: 220 }}>
                        {Object.entries(discountLabels).map(([key, label]) => (
                            <MenuItem value={key} key={key}>{label}</MenuItem>
                        ))}
                    </TextField>

                    {discountType !== "free_delivery" && (
                        <TextField
                            label={discountType === "flat" ? "Amount Off (HK$)" : "Percent Off (%)"}
                            type="number"
                            variant="outlined"
                            size="small"
                            value={discountValue}
                            onChange={(e) => setDiscountValue(Number(e.target.value))}
                            sx={{ maxWidth: 200 }}
                        />
                    )}

                    {discountType === "percent" && (
                        <TextField label="Max Discount (HK$, optional)" type="number" variant="outlined" size="small" value={maxDiscountAmount} onChange={(e) => setMaxDiscountAmount(e.target.value)} sx={{ maxWidth: 220 }} helperText="Caps how much a % discount can be worth" />
                    )}
                </div>

                {discountType === "free_delivery" && (
                    <p className="text-xs text-amber-600 bg-amber-50 border border-amber-200 rounded p-2">
                        Note: delivery is currently free storewide, so this code won't have a visible effect until a real delivery fee exists.
                    </p>
                )}

                <div className="flex flex-col sm:flex-row gap-4">
                    <TextField label="Minimum Order Value (HK$)" type="number" variant="outlined" size="small" value={minOrderValue} onChange={(e) => setMinOrderValue(Number(e.target.value))} sx={{ maxWidth: 220 }} />
                    <TextField label="Expiry Date (optional)" type="date" variant="outlined" size="small" InputLabelProps={{ shrink: true }} value={expiryDate} onChange={(e) => setExpiryDate(e.target.value)} sx={{ maxWidth: 220 }} />
                </div>

                <div className="flex flex-col sm:flex-row gap-4">
                    <TextField label="Total Usage Limit (optional)" type="number" variant="outlined" size="small" value={usageLimitTotal} onChange={(e) => setUsageLimitTotal(e.target.value)} helperText="Across all customers combined — blank means unlimited" sx={{ maxWidth: 260 }} />
                    <TextField label="Uses Per Customer" type="number" variant="outlined" size="small" value={usageLimitPerUser} onChange={(e) => setUsageLimitPerUser(Number(e.target.value))} sx={{ maxWidth: 200 }} />
                </div>

                <FormControlLabel
                    control={<Checkbox checked={active} onChange={(e) => setActive(e.target.checked)} />}
                    label="Active"
                />

                <div className="flex justify-end">
                    <input form="promocodeform" type="submit" className="bg-primary-orange uppercase px-6 py-3 text-white font-medium rounded shadow hover:shadow-lg cursor-pointer" value={isEditMode ? "Save Changes" : "Create Promo Code"} />
                </div>

            </form>
        </>
    );
};

export default PromoCodeForm;
